/**
 * HTML sanitization helpers for safely rendering user-supplied / AI-generated
 * content via React rendering or contentEditable .innerHTML assignments.
 *
 * Uses isomorphic-dompurify so it works in both server and browser contexts.
 */
import DOMPurify from 'isomorphic-dompurify';

/**
 * Sanitize an arbitrary HTML string for safe rendering.
 *
 * - Strips <script>, <iframe>, <object>, <embed>, <form>
 * - Strips inline event handlers (onerror/onload/onclick/etc.)
 * - Restricts URI schemes to safe protocols (http/https/mailto/tel)
 */
export const sanitizeHtml = (dirty: string): string => {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'style'],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  });
};

/**
 * A more permissive sanitizer that ALLOWS inline style attributes - needed by
 * the email/newsletter builders, which rely on inline styles for layout in the
 * resulting email HTML. Still blocks scripts, event handlers, and dangerous
 * URI schemes.
 */
export const sanitizeHtmlWithStyles = (dirty: string): string => {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus'],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  });
};

/**
 * Validate a user-supplied URL - only http/https/mailto/tel are allowed.
 * Returns the canonicalized URL or '#' if the URL is unsafe / malformed.
 */
export const safeUrl = (raw: string): string => {
  if (!raw) return '#';
  try {
    // Recognise an existing scheme so `mailto:`/`tel:` keep their protocol and
    // dangerous schemes (`javascript:`/`data:`) are rejected by it — while a
    // bare host still defaults to https. A real scheme has no dots, unlike a
    // bare "host.com:8080", so the dot-free char class avoids mis-reading a
    // host:port as a scheme. Case-insensitive so "HTTPS://…" isn't mangled.
    const hasScheme = /^[a-z][a-z0-9+-]*:/i.test(raw);
    const u = new URL(hasScheme ? raw : `https://${raw}`);
    return ['http:', 'https:', 'mailto:', 'tel:'].includes(u.protocol.toLowerCase())
      ? u.toString()
      : '#';
  } catch {
    return '#';
  }
};

export default sanitizeHtml;
